import React, { Component, ReactElement } from 'react';
import has from 'lodash/has';
import throttle from 'lodash/throttle';
import isEmpty from 'lodash/isEmpty';

import styles from 'buyside/components/presentation/Feed/FeedItemsPanel/feedItemsPanelStyles.scss';

import StatusUpdateForm from 'buyside/components/presentation/StatusUpdateForm/statusUpdateForm';
import Tabs, { TabContent, TabNav } from 'buyside/components/presentation/Tabs/tabsComponents';
import FeedItems from 'buyside/components/presentation/Feed/FeedItems/feedItems';
import FeedItemLoading from 'buyside/components/presentation/Feed/FeedItem/feedItemLoading';
import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import FundProfileSections from 'buyside/components/presentation/FundProfileShowPage/FundProfileSection/FundProfileSections';
import DelayedAccessAlert, {
  Location
} from 'buyside/components/presentation/AccessControl/delayedAccessAlert';

import { onboardingUrl, userWatchlistUrl } from 'buyside/util/urlHelper';

import {
  AllocatorProfileRecommendationsQuery,
  CreatePostMutationFn,
  CurrentUserQuery,
  FeedQuery,
  FeedQueryResult,
  FundProfileRecommendationsQuery,
  UpdateFundProfileMutationFn
} from 'interfaces/graphql-typings';

interface IProps {
  allocatorRecommendations: AllocatorProfileRecommendationsQuery['allocatorRecommendations'];
  allocatorRecommendationsLoading: boolean;
  allocatorRecommendationsError: Error;
  currentUser: NonNullable<CurrentUserQuery['currentUser']>;
  error: Error;
  feedItems: FeedQuery['feedItems'];
  feedType: string;
  fundRecommendations: FundProfileRecommendationsQuery['fundRecommendations'];
  fundRecommendationsLoading: boolean;
  fundRecommendationsError: Error;
  loading: boolean;
  showPostSubmission: boolean;
  showUpdateSubscriptionsFeedItem: boolean;
  tickerTagString: string;
  handlePost: CreatePostMutationFn;
  loadMoreEntries: () => Promise<FeedQueryResult>;
  handleUpdateFundProfile: UpdateFundProfileMutationFn;
  updateSectionVisibility: (section: string, sectionVisibility: string) => void;
}

class FeedItemsPanel extends Component<IProps, { isLoadingEntries: boolean }> {
  secondLoad?: number;
  maybeLoadMoreEntries: () => void;
  resetInfiniteScrollTimer?: number;
  infiniteScrollThreshold?: number;

  constructor(props: IProps) {
    super(props);
    this.state = { isLoadingEntries: false };
    this.maybeLoadMoreEntries = this.createMaybeLoadMoreEntries();
    this.resetEventListeners = this.resetEventListeners.bind(this);
    this.resetInfiniteScrollThreshold = this.resetInfiniteScrollThreshold.bind(this);
    window.addEventListener('resize', this.resetEventListeners);
  }

  componentDidUpdate(prevProps: IProps): void {
    const MIN_ITEMS_TO_DISPLAY = 8;
    const { feedItems, loading } = this.props;
    if (
      (!prevProps.feedItems && feedItems) ||
      (feedItems &&
        feedItems.pageInfo.hasNextPage &&
        (feedItems?.edges?.length || 0) < MIN_ITEMS_TO_DISPLAY)
    ) {
      // trigger an automatic load of the second page, just the first time the feed loads
      // or the first time the ideas only filter is applied
      // timeout is to prevent batching
      this.secondLoad = window.setTimeout((): void => this.handleLoadMoreEntries(), 100);
    }
    if (prevProps.feedItems !== feedItems) {
      this.resetInfiniteScrollTimer = window.setTimeout(this.resetInfiniteScrollThreshold, 400);
      window.addEventListener('scroll', this.maybeLoadMoreEntries);
    } else if (prevProps.loading === false && loading === true) {
      window.removeEventListener('scroll', this.maybeLoadMoreEntries);
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.resetEventListeners);
    window.clearTimeout(this.resetInfiniteScrollTimer);
    window.clearTimeout(this.secondLoad);
  }

  handleLoadMoreEntries(): void {
    if (this.state.isLoadingEntries || !has(this.props, 'feedItems.pageInfo.hasNextPage')) return;

    this.setState({ isLoadingEntries: true });
    if (this.props.feedItems?.pageInfo.hasNextPage) {
      this.props.loadMoreEntries().then((): void => {
        this.resetInfiniteScrollThreshold();
        if (this.state.isLoadingEntries) this.setState({ isLoadingEntries: false });
      });
    }
  }

  createMaybeLoadMoreEntries(): () => void {
    return throttle((): void => {
      if (this.infiniteScrollThreshold && window.pageYOffset > this.infiniteScrollThreshold) {
        // @ts-ignore
        if (window.hj) {
          // @ts-ignore
          window.hj('tagRecording', ['activity_feed_infinte_scroll_load_more']);
        }
        this.handleLoadMoreEntries();
      }
    }, 400);
  }

  resetInfiniteScrollThreshold(): number {
    // if (!this.props.feedItems.pageInfo.hasNextPage) return this.infiniteScrollThreshold;
    const clientHeight = document.body.clientHeight;
    const footer = document.getElementById('footer');
    const footerHeight = footer ? footer.offsetHeight : 0;
    const threshold = clientHeight - footerHeight - window.innerHeight * 1.5;
    this.infiniteScrollThreshold = threshold;
    this.maybeLoadMoreEntries = this.createMaybeLoadMoreEntries();
    return threshold;
  }

  resetEventListeners(): void {
    window.removeEventListener('scroll', this.maybeLoadMoreEntries);
    this.resetInfiniteScrollThreshold();
    window.addEventListener('scroll', this.maybeLoadMoreEntries);
  }

  render(): ReactElement {
    const {
      loading,
      showPostSubmission,
      handlePost,
      feedItems,
      currentUser,
      tickerTagString,
      error,
      fundRecommendations,
      fundRecommendationsLoading,
      fundRecommendationsError,
      allocatorRecommendations,
      allocatorRecommendationsLoading,
      allocatorRecommendationsError,
      handleUpdateFundProfile,
      updateSectionVisibility
    } = this.props;
    const anyLoading = loading || fundRecommendationsLoading || allocatorRecommendationsLoading;
    const anyError = error || fundRecommendationsError || allocatorRecommendationsError;

    if (anyError) throw error;

    const termsAccepted = currentUser.acceptedPostTerms;

    const mobileWatchlistOptions = (
      <>
        <div className={styles.feedItemsPanelHeader}>
          <span>Activity Feed</span>
          <Button
            type={BUTTON_TYPES.LINK}
            size={BUTTON_SIZES.TINY}
            text="Update Subscriptions"
            url={onboardingUrl()}
          />
        </div>
        <div className={styles.feedItemsPanelHeader}>
          <span>Watchlist</span>
          <Button
            type={BUTTON_TYPES.LINK}
            size={BUTTON_SIZES.TINY}
            text="Update Watchlist"
            url={userWatchlistUrl()}
          />
        </div>
      </>
    );

    return (
      <div>
        {currentUser.adminFundProfiles.length ? (
          <div className={styles.fundProfileReturnsEdit}>
            <h3>Your Fund Returns</h3>
            <Tabs>
              {currentUser.adminFundProfiles.length > 1 ? (
                <TabNav className={styles.tabNav}>
                  {currentUser.adminFundProfiles.map(fundProfile => (
                    <div key={`tab-nav-${fundProfile.id}`}>{fundProfile.name}</div>
                  ))}
                </TabNav>
              ) : null}
              <TabContent>
                {currentUser.adminFundProfiles.map(fundProfile => (
                  <FundProfileSections
                    key={`tab-content-${fundProfile.id}`}
                    fundProfileId={fundProfile.id}
                    pageIsActivityFeed
                    sectionProps={(
                      section: string
                    ): {
                      [key: string]:
                        | boolean
                        | string
                        | IProps['currentUser']['adminFundProfiles'][number]
                        | UpdateFundProfileMutationFn
                        | ((section: string, sectionVisibility: string) => void);
                    } => ({
                      fundProfile: fundProfile,
                      roleIsAdmin: true,
                      sectionCompletenessPercent: JSON.parse(
                        String(fundProfile.sectionCompletenessPercents)
                      )[section],
                      sectionVisibility: JSON.parse(String(fundProfile.sectionVisibilities))[
                        section
                      ],
                      title: fundProfile.name || '',
                      updateFundProfile: handleUpdateFundProfile,
                      updateVisibility: (sectionVisibility: string) =>
                        updateSectionVisibility(section, sectionVisibility)
                    })}
                    showUserDetails
                    visibleSections={['returns']}
                  />
                ))}
              </TabContent>
            </Tabs>
          </div>
        ) : null}
        {showPostSubmission && currentUser.canPost && (
          <StatusUpdateForm
            profileImageUrl={currentUser.profileImageUrl}
            onSubmit={handlePost}
            termsAccepted={termsAccepted}
            tickerTagString={tickerTagString}
            isAdmin={Boolean(currentUser.isAdmin)}
          />
        )}
        <DelayedAccessAlert id={Location.feedItemsPanel} />
        {this.props.showUpdateSubscriptionsFeedItem ? mobileWatchlistOptions : ''}
        {isEmpty(feedItems) && anyLoading ? (
          <FeedItemLoading times={3} />
        ) : (
          <FeedItems
            feedItems={feedItems}
            currentUser={currentUser}
            feedType={null}
            isLoadingEntries={this.state.isLoadingEntries}
            recommendations={fundRecommendations || allocatorRecommendations}
            isPublic={false}
          />
        )}
      </div>
    );
  }
}

export default FeedItemsPanel;
